import { useEffect, useState, useMemo } from "react";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";

// APIs
import { fetchCustomersByOrgId} from "../Api/InvoiceApi";
import { getAllInvoices } from "../Api/ViewPostedInvoice";
import { saveAdvancePayment } from "../Api/ViewPostedInvoice";

const AdvancePayment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const orgId = user?.organisationId;

  const resolvedUserId =
    user?.userId ??
    user?.UserId ??
    user?.id ??
    Number(localStorage.getItem("userId")) ??
    localStorage.getItem("username") ??
    "0";

  const ready = useMemo(() => Boolean(orgId), [orgId]);

  // form fields
  const [customerId, setCustomerId] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [notes, setNotes] = useState("");
  const [reference, setReference] = useState("");

  // dropdown data
  const [customers, setCustomers] = useState([]);
  const [references, setReferences] = useState([]); // filtered invoices

  // loading states
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false); // ✅ ADDED
  const [saving, setSaving] = useState(false);

  // ✅ fetch customers
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    (async () => {
      setLoadingCustomers(true);
      try {
        const res = await fetchCustomersByOrgId(orgId);
        const arr = Array.isArray(res) ? res : res?.data || [];
        console.log(res);
        const normalized = arr
          .map((r) => ({
            code: r?.customerCode ?? "",
            name: r?.name ?? "",
          }))
          .filter((x) => x.code);

        if (!cancelled) {
          setCustomers(normalized);
          if (normalized.length === 0)
            toast.info("No customers found for your organisation.");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Failed to load customers");
      } finally {
        if (!cancelled) setLoadingCustomers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, orgId]);

  // ✅ fetch all invoices once (for filtering)
  const [allInvoices, setAllInvoices] = useState([]);
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    (async () => {
      setLoadingInvoices(true);
      try {
        const res = await getAllInvoices();
        const arr = Array.isArray(res) ? res : res?.data || [];
        if (!cancelled) setAllInvoices(arr);
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Failed to load invoices");
      } finally {
        if (!cancelled) setLoadingInvoices(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  // ✅ filter invoices based on selected customer
  useEffect(() => {
    if (!customerId) {
      setReferences([]);
      setReference("");
      return;
    }

    const filtered =
      allInvoices
        ?.filter(
          (inv) =>
            String(inv?.customerCode ?? "") === String(customerId)
        )
        ?.map((inv) => ({
          invoiceId:
            inv?.invoiceId ??
            inv?.InvoiceId ??
            inv?.id ??
            inv?.Id ??
            "",
        }))
        ?.filter((x) => x.invoiceId) ?? [];

    setReferences(filtered);
    setReference("");
  }, [customerId, allInvoices]);

  const resetForm = () => {
    setCustomerId("");
    setDescription("");
    setAmount("");
    setPaymentMode("");
    setNotes("");
    setReference("");
  };

  const validate = () => {
    if (!orgId) {
      toast.error("Organisation Id is missing.");
      return false;
    }
    if (!customerId) {
      toast.error("Customer Name is required.");
      return false;
    }
    if (!description.trim()) {
      toast.error("Description of Supply is required.");
      return false;
    }
    const amt = Number(amount);
    if (!amount || Number.isNaN(amt)) {
      toast.error("Amount must be a number.");
      return false;
    }
    if (amt <= 0) {
      toast.error("Amount must be greater than 0.");
      return false;
    }
    if (!paymentMode) {
      toast.error("Payment Mode is required.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const selectedCustomerName =
      customers.find((c) => String(c.code) === String(customerId))?.name ?? "";

    const payload = {
      organisationId: Number(orgId),
      userId: Number(resolvedUserId),
      customerName: selectedCustomerName,
      DescriptionOfSupply: description.trim(),
      AmountReceived: Number(amount),
      paymentMode,
      notes: notes.trim(),
      reference,
      createdBy:String(resolvedUserId)
    };

    setSaving(true);
    try {
      await saveAdvancePayment(payload);
      toast.success("Advance payment saved successfully!");
      resetForm();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to save advance payment.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const paymentOptions = [
    { value: "online", label: "Online" },
    { value: "cash", label: "Cash" },
  ];

  if (!ready) {
    return (
      <div className="main-content expand">
        <div className="loading-overlay">
          <Sample14 />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Advance Payment</h2>

          <Form onSubmit={handleSubmit} className="fields-form">
            {/* Organisation + User */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Organisation Id</Form.Label>
                  <Form.Control type="text" value={orgId ?? ""} disabled />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>User Id</Form.Label>
                  <Form.Control type="text" value={resolvedUserId ?? ""} disabled />
                </Form.Group>
              </Col>
            </Row>

            {/* Customer + Description */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Customer Name <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Select
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    disabled={loadingCustomers}
                    required
                  >
                    <option value="">
                      {loadingCustomers
                        ? "Loading customers..."
                        : "Select Customer"}
                    </option>
                    {customers.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} {c.name ? `- ${c.name}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingCustomers && (
                    <div className="mt-2">
                      <Spinner animation="border" size="sm" /> Fetching customers…
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Remarks<span className="text-red">**</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Amount + Payment Mode */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Amount Received <span className="text-red">**</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Payment Mode <span className="text-red">**</span>
                  </Form.Label>
                  <Form.Select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    required
                  >
                    <option value="">Select Mode</option>
                    {paymentOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
                {/**/}
            {/* Notes + Reference */}
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter any additional notes"
                  />
                </Form.Group>
              </Col>
              
              <Col md={4}>
             <Form.Group className="mb-3">
                  <Form.Label>Reference of Payment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Enter Payment Reference"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Buttons */}
            <div className="fields-btn-container">
              <Button
                variant="secondary"
                type="button"
                className="me-2"
                onClick={resetForm}
                disabled={saving}
              >
                Reset
              </Button>

              <Button
                variant="primary"
                type="submit"
                className="fields-submit me-2"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Payment"}
              </Button>

              <Button
                variant="outline-primary"
                type="button"
                onClick={() => navigate("/viewadvancepayments")}
              >
                View Payments
              </Button>
            </div>
          </Form>
        </Container>
      </div>

      {(saving || loadingInvoices) && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default AdvancePayment;
